// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { createAxios } from 'ts/createAxios';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { HistoryEmissions } from 'components/organisms/HistoryEmissions';
import { HistoryCollects } from 'components/organisms/HistoryCollects';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  EdiStatus,
  EmissionStatus,
  GetEmissionParams,
  ItemType,
  Site,
} from 'util/Types';
import { TabletTab } from 'components/molecules/TabletTab';
import { useTranslation } from 'react-i18next';
import styles from './EmissionHistories.module.scss';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from 'components/molecules/SelectFieldMultipleChoise';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import {
  getFormatDateWithCurrentTimezone,
  getSlaFormatYearMonthDate,
  getUtcFormatDateTime,
} from 'ts/formatDate';
import Csv from 'util/csv';
import {
  DATE_FORMAT,
  DEFAULT_PAGE,
  EXPORT_MAX_SIZE,
} from 'util/ConstantValues';
import { OperatorType, PrintLabel, RoleType } from 'util/Enums';
import { getEdiStatusLabel, mergeClasses, normalizeNumber } from 'util/commons';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getCommonSites, getCommonWasteUnitNames } from 'apis/commons';
import { useHistory } from 'react-router';
import { setIsExporting } from 'redux/slices/historiesCsvExportSlice';
import { store } from 'redux/store';
import { Id, toast } from 'react-toastify';
import { WorkHistoriesEmissionsData } from 'types/workHistoriesTypes';
import { handleError } from 'util/errorHandler';
import _omit from 'lodash/omit';
import { getEmissionWorkHistories } from 'apis/work_histories/emission';
import { getCommonTenantNames } from 'apis/tenants';
import { getTemporaryStorages } from 'apis/temporary_storage';
import _debounce from 'lodash/debounce';

const PRINT_WIDTH = 640;

const EmissionHistories = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const api = createAxios();
  const user = useSelector(selectUser);
  const [sites, setSites] = useState<Site[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(
    history.location.hash === '#1' ? 1 : 0
  );

  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [currentEmission, setCurrentEmission] =
    useState<WorkHistoriesEmissionsData | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [itemOptions, setItemOptions] = useState<OptionItem[]>([]);
  const [tenantOptions, setTenantOptions] = useState<OptionItem[]>([]);
  const [siteOptions, setSiteOptions] = useState<OptionItem[]>([]);
  const [temporaryStorageOptions, setTemporaryStorageOptions] = useState<
    OptionItem[]
  >([]);
  const [tmpFilter, setTmpFilter] = useState<GetEmissionParams>({});
  const [filter, setFilter] = useState({});
  const dispatch = useDispatch();
  const [contentHeight, setContentHeight] = useState<number>();

  const isAndroid = () => {
    return /Android/i.test(navigator.userAgent);
  };

  const storageViewable = useMemo(
    () =>
      tabIndex === 0 &&
      user.role !== RoleType.AdminTenant &&
      (user.role === RoleType.AdminOperator || user.temporaryStorageEnabled),
    [tabIndex, user]
  );

  /*
   * Should only be called when user is (1) on Android or (2) has PrintLabel.QRCode type
   */
  const handlePrintLabel = async (emission: WorkHistoriesEmissionsData) => {
    let printableEmission = {
      ...emission.emission,
      emissionId: emission.id,
      printLabel: user.printLabel,
      datetime: getFormatDateWithCurrentTimezone(
        emission.emission.datetime,
        DATE_FORMAT.slaYMDHms
      ),
    };

    if (
      emission.emission.emissionStatus !== EmissionStatus.Close &&
      user.printLabel === PrintLabel.QRCode &&
      !emission.emission.qrCode
    ) {
      try {
        const api = createAxios();
        const { data } = await api.get(`/emissions/${emission.id}/qr-code`);
        printableEmission = { ...printableEmission, qrCode: data.qrCode };
      } catch (error) {
        handleError(error);
        return;
      }
    }

    if (user.printLabel === PrintLabel.QRCode && printableEmission.qrCode) {
      setQrCodeData(printableEmission.qrCode);
      setCurrentEmission(emission);
      setShowQRModal(true);
      return;
    }

    window.printEmissionLabel(
      JSON.stringify({
        ...printableEmission,
        weight: normalizeNumber({ value: printableEmission.weight }),
        measuredWeight: normalizeNumber({
          value: printableEmission.measuredWeight,
        }),
        containerWeight: normalizeNumber({
          value: printableEmission.containerWeight,
        }),
      }),
      i18n.language
    );
  };

  const handlePrintFromModal = async () => {
    if (!currentEmission) return;

    const printableEmission = {
      ...currentEmission.emission,
      emissionId: currentEmission.id,
      printLabel: user.printLabel,
      datetime: getFormatDateWithCurrentTimezone(
        currentEmission.emission.datetime,
        DATE_FORMAT.slaYMDHms
      ),
      qrCode: qrCodeData,
    };

    if (isAndroid()) {
      window.printEmissionLabel(
        JSON.stringify({
          ...printableEmission,
          weight: normalizeNumber({ value: printableEmission.weight }),
          measuredWeight: normalizeNumber({
            value: printableEmission.measuredWeight,
          }),
          containerWeight: normalizeNumber({
            value: printableEmission.containerWeight,
          }),
        }),
        i18n.language
      );
      setShowQRModal(false);
    } else {
      if (modalContentRef.current) {
        try {
          const clonedContent = modalContentRef.current.cloneNode(
            true
          ) as HTMLElement;

          clonedContent.style.backgroundColor = '#FFFFFF';

          const textElements = clonedContent.querySelectorAll('span, div, p');
          textElements.forEach((el) => {
            (el as HTMLElement).style.color = '#000000';
          });

          const qrCodeContainer =
            clonedContent.querySelector(`.qrCodeContainer`);
          if (qrCodeContainer && qrCodeContainer instanceof HTMLElement) {
            qrCodeContainer.style.backgroundColor = '#FFFFFF';
          }

          const button = clonedContent.querySelector('button');
          if (button) {
            button.remove();
          }

          clonedContent.style.position = 'absolute';
          clonedContent.style.left = '-9999px';
          document.body.appendChild(clonedContent);

          const exportHeight = Math.max(
            PRINT_WIDTH * Math.sqrt(2),
            clonedContent.scrollHeight
          );

          html2canvas(clonedContent, {
            backgroundColor: '#FFFFFF',
            width: PRINT_WIDTH,
            height: exportHeight,
            windowWidth: PRINT_WIDTH,
            windowHeight: exportHeight,
            scale: 2,
          })
            .then((canvas) => {
              try {
                document.body.removeChild(clonedContent);

                const pdf = new jsPDF('portrait', 'px', [
                  canvas.width,
                  canvas.height,
                ]);

                const logoImg = new Image();
                logoImg.src = 'logo670x240.png';
                pdf.addImage(logoImg, 'PNG', 82, 100, 0, 0);

                const imgData = canvas.toDataURL('image/png');
                pdf.addImage(
                  imgData,
                  'PNG',
                  Math.abs(canvas.width / 2 - PRINT_WIDTH) / 2 + 50,
                  240,
                  canvas.width,
                  canvas.height
                );
                pdf.save(`emission-label-${currentEmission.id}.pdf`);

                toast.success(t('history_emission_card.pdf_export_success'));

                setShowQRModal(false);
              } catch (error) {
                if (document.body.contains(clonedContent)) {
                  document.body.removeChild(clonedContent);
                }

                console.error('Error generating PDF:', error);
                toast.error(t('history_emission_card.pdf_export_error'));
              }
            })
            .catch((error) => {
              if (document.body.contains(clonedContent)) {
                document.body.removeChild(clonedContent);
              }

              console.error('Error capturing content:', error);
              toast.error(t('history_emission_card.pdf_export_error'));
            });
        } catch (error) {
          console.error('Error preparing content for PDF:', error);
          toast.error(t('history_emission_card.pdf_export_error'));
        }
      } else {
        toast.error(t('history_emission_card.pdf_export_error'));
      }
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (modalRef.current && e.target === modalRef.current) {
      setShowQRModal(false);
    }
  };

  const callSiteApi = async () => {
    const res = await api.get('/emissions/sites');
    setSites(res.data);
  };

  const onChangeFilter = (key: string, value: string[]) => {
    setTmpFilter({ ...tmpFilter, [key]: value });
  };

  const headers: [string, string][] = useMemo(() => {
    return [
      ['emitting_date', t('histories.label_csv_emitting_date')],
      ['emitting_time', t('histories.label_csv_emitting_time')],
      ['site_name', t('histories.label_csv_emissions_site_name')],
      ['group1', t('histories.label_csv_group1')],
      ['group2', t('histories.label_csv_group2')],
      tabIndex === 0 && [
        'tenant_code_string',
        t('histories.label_csv_tenant_code'),
      ],
      ['tenant', t('histories.label_csv_tenant')],
      ['brand_owner', t('histories.label_csv_brand_owner')],
      ['category', t('histories.label_csv_category')],
      ['item', t('histories.label_csv_item')],
      ['registration_id', t('histories.label_csv_registration_id')],
      ['weight_or_quantity', t('histories.label_csv_weight_or_quantity')],
      ['container', t('histories.label_csv_container')],
      [
        'quantity_in_case_of_unscalable',
        t('histories.label_csv_quantity_in_case_of_unscalable'),
      ],
      ['weight_kg', t('histories.label_csv_weight_kg')],
      [
        'collection_request_datetime',
        t('histories.label_csv_collection_request_date_time'),
      ],
      ['collection_request_by', t('histories.label_csv_collection_request_by')],
      ['processing_method', t('histories.label_csv_processing_method')],
      ['co2_emission_factor', t('histories.label_csv_CO2_emission_factor')],
      ['collecting_company', t('histories.label_csv_collecting_company')],
      tabIndex === 1 && [
        'collecting_datetime',
        t('histories.label_csv_collecting_date_time'),
      ],
      tabIndex === 1 && ['edi_id', t('histories.label_edi_id')],
      tabIndex === 1 && [
        'manifest_number',
        t('histories.label_manifest_number'),
      ],
      storageViewable && [
        'temporary_storage',
        t('history_emission_card.card_temporary_storage'),
      ],
      storageViewable && [
        'storage_updated_at',
        t('emission_qr_scanning.storage_updated_at'),
      ],
    ].filter(Boolean) as [string, string][];
  }, [t, tabIndex, user, storageViewable]);

  const getCsvData = (data: WorkHistoriesEmissionsData[]) => {
    return data.map((emission) => ({
      emitting_date: getFormatDateWithCurrentTimezone(
        emission.emission.datetime,
        DATE_FORMAT.full_date
      ),
      emitting_time: getFormatDateWithCurrentTimezone(
        emission.emission.datetime,
        DATE_FORMAT.hhss
      ),
      site_name: emission.emission.site,
      group1: emission.emission.section,
      group2: emission.emission.floor,
      ...(tabIndex === 0 && {
        tenant_code_string: emission.emission.tenantCode,
      }),
      tenant: emission.emission.tenant,
      brand_owner: emission.emission.brandOwner,
      category: emission.emission.itemCategory,
      item: emission.emission.item,
      registration_id: emission.id,
      weight_or_quantity:
        emission.emission.itemType === ItemType.FIX
          ? t('common.fixed')
          : t('common.optional'),
      container: emission.emission.tareInformation || '',
      quantity_in_case_of_unscalable:
        emission.emission.itemType === ItemType.FIX &&
        emission.emission.quantity !== ''
          ? emission.emission.quantity
          : '',
      weight_kg: emission.emission.weight || '',
      collection_request_datetime: emission.request.datetime
        ? getFormatDateWithCurrentTimezone(
            emission.request.datetime,
            DATE_FORMAT.slaYMDHms
          )
        : '',
      collection_request_by: emission.request.userName,
      processing_method: emission.emission.recycleMethod,
      co2_emission_factor:
        typeof emission.emission.emissionFactor === 'object'
          ? emission.emission.emissionFactor.emissionFactorValue
          : emission.emission.emissionFactorValue || '',
      collecting_company: emission.collect.operatorName,
      ...(tabIndex === 1 && {
        collecting_datetime: getFormatDateWithCurrentTimezone(
          emission.collect.datetime,
          DATE_FORMAT.slaYMDHms
        ),
        edi_id:
          getEdiStatusLabel({
            t,
            status: emission.collect.ediStatus,
            operatorType: OperatorType.Emissions,
          }) || '',
        manifest_number:
          emission.collect.ediStatus != null &&
          ![EdiStatus.None, EdiStatus.Failed, EdiStatus.Processing].includes(
            emission.collect.ediStatus
          )
            ? String(emission.collect.ediNumber)
            : '',
      }),
      ...(storageViewable && {
        temporary_storage: emission.emission.temporaryStorage?.name || '',
        storage_updated_at: emission.emission.storageUpdatedAt
          ? getFormatDateWithCurrentTimezone(
              emission.emission.storageUpdatedAt,
              DATE_FORMAT.slaYMDHm
            )
          : '',
      }),
    }));
  };

  const handleDownLoadCsv = async () => {
    const toastId: Id = toast.info(t('messages.M_file_export_started'));
    dispatch(setIsExporting(true));

    getEmissionWorkHistories({
      type: tabIndex === 0 ? 'requests' : 'collected',
      axiosConfigs: {
        notShowLoading: true,
      },
      params: {
        ..._omit(filter, ['trigger']),
        ...(user.role !== RoleType.AdminOperator && {
          siteId: sites[0]?.childSites[0]?.id,
        }),
        page: DEFAULT_PAGE,
        take: EXPORT_MAX_SIZE,
      },
      successCallback: (data) => {
        const fileName = `${t('histories.title')} （${
          tabIndex === 0
            ? t('histories.tabs.emission_registration_or_collect_request')
            : t('histories.tabs.emission_collected')
        }）`;

        if (store.getState().historiesCsvExport.isExporting) {
          toast.success(t('messages.M_file_export_finished'));
          new Csv({
            headers: headers,
            data: getCsvData(
              'emissionsRequest' in data
                ? data.emissionsRequest
                : data.emissionsCollected
            ),
          }).downLoadCsv({
            filename: fileName,
          });
        }
      },
      finallyCallback: () => {
        if (toastId) {
          toast.dismiss(toastId);
        }
        dispatch(setIsExporting(false));
      },
    });
  };

  const tenantCustomOptions = useMemo(() => {
    // AdminTenant won't use tenant filter
    if (user.role === RoleType.AdminTenant) {
      return tenantOptions;
    }

    const unidentifyTenant: OptionItem = {
      id: '',
      name: t('un_collect.unidentify_tenant'),
    };

    return tenantOptions.concat(unidentifyTenant);
  }, [tenantOptions, t]);

  const tempStorageCustomOptions = useMemo(() => {
    const notSetOption: OptionItem = {
      id: '',
      name: t('un_collect.not_set_temp_storage'),
    };

    return temporaryStorageOptions.concat(notSetOption);
  }, [temporaryStorageOptions, t]);

  useEffect(() => {
    getCommonWasteUnitNames({
      getAll: true,
      successCallback: (data) => {
        setItemOptions(
          data.map((item) => ({
            id: item.name,
            name: item.name,
          }))
        );
      },
    });

    user.role !== RoleType.AdminTenant &&
      getCommonTenantNames({
        getAll: true,
        successCallback: (data) => {
          const tenantNames = data.map((item) => item.name);
          const duplicatedNames = tenantNames.filter(
            (item, index) => tenantNames.indexOf(item) != index
          );

          setTenantOptions(
            data
              .filter(({ name }) => !duplicatedNames.includes(name))
              .map((tenant) => ({
                id: tenant.name,
                name: tenant.name,
              }))
          );
        },
      });

    user.role === RoleType.AdminOperator &&
      getCommonSites({
        successCallback: (data) => {
          setSiteOptions(
            data.map((item) => ({
              id: item.id,
              name: item.name,
            }))
          );
        },
      });
  }, [user]);

  useEffect(() => {
    callSiteApi();

    if (user.role !== RoleType.AdminTenant) {
      getTemporaryStorages({
        successCallback: (data) => {
          setTemporaryStorageOptions(
            data.map(({ id, name }) => ({ id, name }))
          );
        },
      });
    }
  }, [user]);

  useLayoutEffect(() => {
    const calculateContentHeight = () => {
      const header = document.querySelector('header.nav');
      const filter = document.querySelector(`.${styles.filter}`);
      const tab = document.querySelector('[class^="TabletTab_tabMenu"]');

      setContentHeight(
        window.innerHeight -
          (header?.clientHeight || 0) -
          (filter?.clientHeight || 0) -
          (tab?.clientHeight || 0) -
          1
      );
    };

    calculateContentHeight();
    const debouncedCalculateContentHeight = _debounce(
      calculateContentHeight,
      200
    );

    window.addEventListener('resize', debouncedCalculateContentHeight);

    return () => {
      window.removeEventListener('resize', debouncedCalculateContentHeight);
    };
  }, []);

  return (
    <div>
      <PrimaryTemplate
        h1={t('histories.title')}
        className={styles.history}
        download={handleDownLoadCsv}
      >
        <div className={styles.filter}>
          <div
            className={mergeClasses(styles.filterWrapper, {
              [styles.filterWrapperNormal]:
                user.role !== RoleType.AdminOperator,
              [styles.requestTab]: storageViewable,
            })}
          >
            {user.role === RoleType.AdminOperator && (
              <div className={styles.filterBox}>
                <SelectFieldMultipleChoise
                  options={siteOptions}
                  name="Sites"
                  searchable={true}
                  placeholder={t('common.site') ?? ''}
                  onChange={(sites) =>
                    onChangeFilter(
                      'siteIds',
                      sites.map((item) => String(item.id))
                    )
                  }
                  selectedOptions={tmpFilter.siteIds || []}
                  darkMode
                  allowClear
                />
              </div>
            )}

            <div className={styles.filterBox}>
              <SelectFieldMultipleChoise
                options={itemOptions}
                name="Items"
                searchable={true}
                placeholder={t('common.item') ?? ''}
                onChange={(items) =>
                  onChangeFilter(
                    'wasteUnitNames',
                    items.map((item) => String(item.id))
                  )
                }
                selectedOptions={tmpFilter.wasteUnitNames || []}
                darkMode
                allowClear
              />
            </div>

            {user.role !== RoleType.AdminTenant && (
              <div className={styles.filterBox}>
                <SelectFieldMultipleChoise
                  options={tenantCustomOptions}
                  name="Tenants"
                  searchable={true}
                  placeholder={t('tenant.thead_tenant') ?? ''}
                  onChange={(tenants) =>
                    onChangeFilter(
                      'tenantNames',
                      tenants.map((tenant) => String(tenant.id))
                    )
                  }
                  selectedOptions={tmpFilter.tenantNames || []}
                  darkMode
                  allowClear
                />
              </div>
            )}

            {storageViewable && (
              <div className={styles.filterBox}>
                <SelectFieldMultipleChoise
                  options={tempStorageCustomOptions}
                  name="temporaryStorages"
                  placeholder={t('un_collect.temporary_storage') ?? ''}
                  onChange={(storages) =>
                    onChangeFilter(
                      'temporaryStorageIds',
                      storages.map((storage) => String(storage.id))
                    )
                  }
                  selectedOptions={
                    tempStorageCustomOptions.filter((obj) =>
                      tmpFilter?.temporaryStorageIds?.includes(String(obj.id))
                    ) || []
                  }
                  darkMode
                  allowClear
                  searchable
                />
              </div>
            )}

            <div className={styles.filterBox}>
              <TableFilterCalendar
                containerClassName={styles.DateRangePicker}
                expanded={{
                  placeholder: t('common.placeholder.select_date_range'),
                  icon: <IconCalendarTablet />,
                }}
                allowClear={true}
                onChange={(values) => {
                  setTmpFilter((prev) => ({
                    ...prev,
                    dateFrom: values?.[0]
                      ? getSlaFormatYearMonthDate(values[0])
                      : '',
                    dateTo: values?.[1]
                      ? getSlaFormatYearMonthDate(values[1])
                      : '',
                  }));
                }}
                value={[tmpFilter?.dateFrom || '', tmpFilter?.dateTo || '']}
                endDateRequired={true}
                align={storageViewable ? 'right' : 'left'}
                portalPositionPoint={(ref) => ref}
              />
            </div>

            <button
              className={styles.buttonApply}
              onClick={() => {
                setFilter({
                  ...tmpFilter,
                  trigger: Date.now(),
                });
              }}
            >
              {t('dashboard.button_search')}
            </button>
          </div>
        </div>

        <TabletTab
          tabItems={[
            t('histories.tabs.emission_registration_or_collect_request'),
            t('histories.tabs.emission_collected'),
          ]}
          setTabIndex={setTabIndex}
          defaultTabIndex={tabIndex}
          renderContent={({ index }) => {
            return (
              <div className="scroll-contents">
                {index === 0 && (
                  <HistoryEmissions
                    sites={sites}
                    isActive={tabIndex === 0}
                    filter={filter}
                    wrapperStyle={{
                      height: contentHeight,
                    }}
                    onPrintLabel={
                      isAndroid() || user.printLabel === PrintLabel.QRCode
                        ? handlePrintLabel
                        : undefined
                    }
                  />
                )}
                {index === 1 && (
                  <HistoryCollects
                    sites={sites}
                    isActive={tabIndex === 1}
                    filter={filter}
                    wrapperStyle={{
                      height: contentHeight,
                    }}
                  />
                )}
              </div>
            );
          }}
        />
      </PrimaryTemplate>

      {showQRModal && currentEmission && (
        <div
          className={styles.qrModal}
          ref={modalRef}
          onClick={handleModalClick}
        >
          <div ref={modalContentRef} className={styles.qrModalContent}>
            <div className={styles.qrModalMainContainer}>
              <div className={styles.qrCodeContainer} ref={qrCodeRef}>
                <div className={styles.qrCodeBackground}>
                  <QRCode
                    value={qrCodeData}
                    size={200}
                    className={styles.qrCodeStyle}
                  />
                </div>
              </div>

              <div className={styles.qrModalRightContent}>
                <div className={styles.qrModalInfoRowWeight}>
                  <span className={styles.qrModalLabelWeight}>
                    {t('history_emission_card.card_weight')}
                  </span>
                  <span className={styles.qrModalValueWeight}>
                    {normalizeNumber({
                      value: currentEmission.emission.weight,
                    })}{' '}
                    kg
                  </span>
                </div>

                <div className={styles.qrModalInfoRow}>
                  <span className={styles.qrModalLabel}>
                    {t('history_emission_card.card_registration_id')}
                  </span>
                  <span className={styles.qrModalValue}>
                    {currentEmission.id}
                  </span>
                </div>

                <div className={styles.qrModalInfoRow}>
                  <span className={styles.qrModalLabel}>
                    {t('history_emission_card.card_discharge_date_time')}
                  </span>
                  <span className={styles.qrModalValue}>
                    {getUtcFormatDateTime(
                      currentEmission.emission.datetime,
                      DATE_FORMAT.slaYMDHm
                    )}
                  </span>
                </div>

                <div className={styles.qrModalInfoRow}>
                  <span className={styles.qrModalLabel}>
                    {t('history_emission_card.card_site')}
                  </span>
                  <span className={styles.qrModalValue}>
                    {currentEmission.emission.site}
                  </span>
                </div>

                <div className={styles.qrModalInfoRow}>
                  <span className={styles.qrModalLabel}>
                    {t('history_emission_card.card_content')}
                  </span>
                  <span className={styles.qrModalValue}>
                    {currentEmission.emission.description}
                  </span>
                </div>

                {currentEmission.request && (
                  <>
                    <div className={styles.qrModalInfoRow}>
                      <span className={styles.qrModalLabel}>
                        {t(
                          'history_emission_card.card_collection_request_date_time'
                        )}
                      </span>
                      <span className={styles.qrModalValue}>
                        {currentEmission.request.datetime !== ''
                          ? getUtcFormatDateTime(
                              currentEmission.request.datetime,
                              DATE_FORMAT.slaYMDHm
                            )
                          : ' '}
                      </span>
                    </div>

                    <div className={styles.qrModalInfoRow}>
                      <span className={styles.qrModalLabel}>
                        {t('history_emission_card.card_collection_requester')}
                      </span>
                      <span className={styles.qrModalValue}>
                        {currentEmission.request.userId !== ''
                          ? `${currentEmission.request.userId} (${currentEmission.request.userName})`
                          : ''}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={handlePrintFromModal}
              className={styles.qrModalPrintButton}
            >
              {t('history_emission_card.print_label_button')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmissionHistories;
